// Règles du drafter, appliquées sur l'appareil en mode local.
// Miroir de backend/drafter_logic.py, qui fait foi pour les drafts en ligne.

// Ordre officiel des drafts en tournoi : 6 bans, 6 picks, 4 bans, 4 picks.
export const DRAFT_ORDER = [
  ["blue", "ban"], ["red", "ban"], ["blue", "ban"], ["red", "ban"], ["blue", "ban"], ["red", "ban"],
  ["blue", "pick"], ["red", "pick"], ["red", "pick"], ["blue", "pick"], ["blue", "pick"], ["red", "pick"],
  ["red", "ban"], ["blue", "ban"], ["red", "ban"], ["blue", "ban"],
  ["red", "pick"], ["blue", "pick"], ["blue", "pick"], ["red", "pick"],
].map(([side, type]) => ({ side, type }));

export const TURN_SECONDS = 35;
export const SIDES = ["blue", "red"];
const blankTurn = () => ({ selected: { blue: null, red: null } });

export function newDraft({ blue, red, mode, games, timer }) {
  return {
    names: { blue: blue || "Équipe bleue", red: red || "Équipe rouge" },
    mode, games, timer,
    previous_games: [], choices: [],
    ...blankTurn(), ready: { blue: false, red: false }, started: false, deadline: null,
  };
}

export const stepOf = (d) => DRAFT_ORDER[d.choices.length] || null;

// Range les choix (ids de champion dans l'ordre de la draft, null = ban passé) par équipe et par type.
export function boardOf(choices) {
  const board = { blue: { ban: [], pick: [] }, red: { ban: [], pick: [] } };
  choices.forEach((id, i) => board[DRAFT_ORDER[i].side][DRAFT_ORDER[i].type].push(id));
  return board;
}

// Fearless : un champion joué dans une game précédente de la série n'est plus disponible.
export function lockedBy(previousGames) {
  return previousGames.flatMap((g) => g.choices.filter((id, i) => id && DRAFT_ORDER[i].type === "pick"));
}

export function usedBy(d) {
  return new Set([...d.choices.filter(Boolean), ...(d.mode === "fearless" ? lockedBy(d.previous_games) : [])]);
}

function commit(d, champion, base) {
  const next = { ...d, choices: [...d.choices, champion], ...blankTurn() };
  next.deadline = d.timer && stepOf(next) ? base + TURN_SECONDS : null;
  return next;
}

// Joue les tours dont le temps est écoulé : sélection validée, sinon ban passé ou pick au hasard.
export function expire(d, now, champions) {
  let next = d;
  while (next.deadline && now >= next.deadline && stepOf(next)) {
    const { side, type } = stepOf(next);
    const used = usedBy(next);
    const sel = next.selected[side];
    let champ = null;
    if (sel && !used.has(sel)) champ = sel;
    else if (type === "pick") {
      const pool = champions.filter((c) => !used.has(c));
      champ = pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
    }
    next = commit(next, champ, next.deadline);
  }
  return next;
}

// Renvoie la draft après l'action, ou lève une Error dont le message s'affiche au joueur.
export function applyAction(d, side, action, now, champions) {
  const { type } = action;
  if (type === "ready") {
    const ready = { ...d.ready, [side]: true };
    const go = ready.blue && ready.red && !d.started;
    return { ...d, ready, started: d.started || go, deadline: go && d.timer ? now + TURN_SECONDS : d.deadline };
  }
  if (type === "swap") {
    if (stepOf(d)) throw new Error("Les échanges se font une fois la draft terminée.");
    const slots = DRAFT_ORDER.flatMap((s, i) => (s.side === side && s.type === "pick" ? [i] : []));
    const choices = [...d.choices];
    [choices[slots[action.a]], choices[slots[action.b]]] = [choices[slots[action.b]], choices[slots[action.a]]];
    return { ...d, choices };
  }
  if (type === "next_game") {
    if (stepOf(d)) throw new Error("La draft en cours n'est pas terminée.");
    if (d.previous_games.length + 1 >= d.games) throw new Error("La série est terminée.");
    const names = action.swap_sides ? { blue: d.names.red, red: d.names.blue } : d.names;
    return {
      ...d, names, previous_games: [...d.previous_games, { choices: d.choices, names: d.names }],
      choices: [], ...blankTurn(), ready: { blue: false, red: false }, started: false, deadline: null,
    };
  }
  const step = stepOf(d);
  if (!d.started || !step) throw new Error("La draft n'est pas en cours.");
  if (step.side !== side) throw new Error("Ce n'est pas votre tour.");
  if (type === "select") {
    if (!champions.includes(action.champion) || usedBy(d).has(action.champion)) throw new Error("Ce champion n'est pas disponible.");
    return { ...d, selected: { ...d.selected, [side]: action.champion } };
  }
  if (type === "lock") {
    const champ = d.selected[side];
    if (!champ || usedBy(d).has(champ)) throw new Error("Sélectionnez d'abord un champion disponible.");
    return commit(d, champ, now);
  }
  if (type === "skip") {
    if (step.type !== "ban") throw new Error("Seul un ban peut être passé.");
    return commit(d, null, now);
  }
  throw new Error("Action inconnue.");
}

// Mode local uniquement : revient au tour précédent.
export function undo(d, now) {
  if (!d.choices.length) return d;
  return { ...d, choices: d.choices.slice(0, -1), ...blankTurn(), deadline: d.timer && d.started ? now + TURN_SECONDS : null };
}
